import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// ---- Curriculum ----
export const fetchCurriculum = (lang = "en") =>
    api.get(`/curriculum?lang=${lang}`).then((r) => r.data.weeks);
export const fetchWeek = (id, lang = "en") =>
    api.get(`/curriculum/${id}?lang=${lang}`).then((r) => r.data);
export const submitQuiz = (week_id, answers) =>
    api.post("/quiz/submit", { week_id, answers }).then((r) => r.data);
export const fetchProgress = () =>
    api.get("/progress").then((r) => r.data.progress);

// ---- Chat ----
export const sendChat = (message, session_id, lang = "en") =>
    api.post("/chat/tutor", { message, session_id, lang }).then((r) => r.data);

// SSE streaming chat (fetch with reader)
export async function streamChat(
    { message, session_id, lang = "en" },
    { onSession, onDelta, onDone, onError } = {},
) {
    try {
        const res = await fetch(`${API}/chat/tutor/stream`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ message, session_id, lang }),
        });
        if (!res.ok || !res.body) {
            onError?.(new Error("stream failed"));
            return;
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += dec.decode(value, { stream: true });
            let idx;
            while ((idx = buf.indexOf("\n\n")) >= 0) {
                const chunk = buf.slice(0, idx);
                buf = buf.slice(idx + 2);
                for (const line of chunk.split("\n")) {
                    if (!line.startsWith("data:")) continue;
                    const json = line.slice(5).trim();
                    if (!json) continue;
                    try {
                        const ev = JSON.parse(json);
                        if (ev.type === "session") onSession?.(ev.session_id);
                        else if (ev.type === "delta") onDelta?.(ev.text);
                        else if (ev.type === "done") onDone?.();
                        else if (ev.type === "error") onError?.(new Error(ev.message));
                    } catch (err) {
                        // SSE chunk that wasn't valid JSON — log but keep streaming.
                        console.warn("streamChat: dropped malformed SSE chunk", err);
                    }
                }
            }
        }
        onDone?.();
    } catch (e) {
        onError?.(e);
    }
}

// ---- Lesson Plans ----
export const generateLessonPlan = (payload) =>
    api.post("/lesson-plan/generate", payload).then((r) => r.data);
export const fetchMyPlans = () =>
    api.get("/lesson-plans/mine").then((r) => r.data.plans);
export const fetchPlan = (id) =>
    api.get(`/lesson-plans/${id}`).then((r) => r.data);
export const deletePlan = (id) =>
    api.delete(`/lesson-plans/${id}`).then((r) => r.data);

// ---- Classes ----
export const createClass = (name) =>
    api.post("/classes", { name }).then((r) => r.data);
export const fetchMyClasses = () =>
    api.get("/classes/mine").then((r) => r.data.classes);
export const fetchJoinedClasses = () =>
    api.get("/classes/joined").then((r) => r.data.classes);
export const previewClass = (code) =>
    api.get(`/classes/preview/${code}`).then((r) => r.data);
export const joinClass = (code) =>
    api.post("/classes/join", { code }).then((r) => r.data);
export const fetchClassStats = (id) =>
    api.get(`/classes/${id}/stats`).then((r) => r.data);
export const deleteClass = (id) =>
    api.delete(`/classes/${id}`).then((r) => r.data);
export const fetchMyRank = (id) =>
    api.get(`/classes/${id}/my-rank`).then((r) => r.data);
export const classCsvUrl = (id) => `${API}/classes/${id}/export.csv`;

export const submitVideoFeedback = (payload) =>
    api.post("/video-feedback", payload).then((r) => r.data);

// ---- Water Lab ----
export const logWater = (payload) =>
    api.post("/water/log", payload).then((r) => r.data);
export const fetchWaterHistory = () =>
    api.get("/water/history").then((r) => r.data.readings);
export const deleteWater = (id) =>
    api.delete(`/water/${id}`).then((r) => r.data);

// ---- Plant Picker ----
export const pickPlants = (payload) =>
    api.post("/plant-picker", payload).then((r) => r.data);
export const fetchMyPicks = () =>
    api.get("/plant-picks/mine").then((r) => r.data.picks);
export const deletePick = (id) =>
    api.delete(`/plant-picks/${id}`).then((r) => r.data);

// ---- Settings + Leaderboard ----
export const updateSettings = (payload) =>
    api.post("/me/settings", payload).then((r) => r.data);
export const fetchGlobalLeaderboard = (limit = 10) =>
    api.get(`/leaderboard/global?limit=${limit}`).then((r) => r.data.leaders);
export const fetchClassLeaderboard = (class_id) =>
    api.get(`/leaderboard/class/${class_id}`).then((r) => r.data);
export const fetchClassWater = (class_id) =>
    api.get(`/classes/${class_id}/water`).then((r) => r.data);
export const classWaterCsvUrl = (class_id) =>
    `${API}/classes/${class_id}/water.csv`;
export const myWaterCsvUrl = () => `${API}/water/export.csv`;

// ---- Cheers ----
export const sendCheer = (target_user_id) =>
    api.post("/cheers", { target_user_id }).then((r) => r.data);
export const fetchCheerCounts = (user_ids) =>
    api
        .get(`/cheers/counts?user_ids=${encodeURIComponent(user_ids.join(","))}`)
        .then((r) => r.data.counts);
export const fetchCheerStreaks = (user_ids) =>
    api
        .get(`/cheers/streaks?user_ids=${encodeURIComponent(user_ids.join(","))}`)
        .then((r) => r.data.streaks);

// ---- Teacher Watchlist ----
export const fetchTeacherWatchlist = (days = 7) =>
    api.get(`/teacher/watchlist?days=${days}`).then((r) => r.data);

// ---- Billing & Subscription ----
export const fetchBillingPackages = () =>
    api.get("/billing/packages").then((r) => r.data);
export const fetchMyBilling = () =>
    api.get("/billing/me").then((r) => r.data);
export const startTrial = () =>
    api.post("/billing/start-trial").then((r) => r.data);
export const createCheckout = (package_id) =>
    api
        .post("/billing/checkout", {
            package_id,
            origin_url: window.location.origin,
        })
        .then((r) => r.data);
export const fetchCheckoutStatus = (session_id) =>
    api.get(`/billing/status/${encodeURIComponent(session_id)}`).then((r) => r.data);

// ---- Notifications ----
export const fetchNotifications = () =>
    api.get("/notifications").then((r) => r.data);
export const markAllNotificationsRead = () =>
    api.post("/notifications/read-all").then((r) => r.data);
export const fetchMyClassPlans = () =>
    api.get("/me/class-plans").then((r) => r.data.plans);
export const sendThanks = (notification_id) =>
    api.post("/notifications/thanks", { notification_id }).then((r) => r.data);
export const fetchPlanComments = (plan_id) =>
    api.get(`/lesson-plans/${plan_id}/comments`).then((r) => r.data.comments);
export const addPlanComment = (plan_id, body) =>
    api.post(`/lesson-plans/${plan_id}/comments`, { body }).then((r) => r.data);

// ---- Auth ----
export const authRegister = (data) =>
    api.post("/auth/register", data).then((r) => r.data);
export const authLogin = (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authGoogleSession = (session_id, role = "student") =>
    api.post("/auth/google/session", { session_id, role }).then((r) => r.data);
